import { useEffect } from "react";

type Combo = string; // "ctrl+/", "ctrl+k", "esc"

function parse(combo: Combo) {
  const parts = combo.toLowerCase().split("+").map((p) => p.trim());
  const key = parts.pop()!;
  return {
    key,
    ctrl: parts.includes("ctrl") || parts.includes("cmd"),
    shift: parts.includes("shift"),
    alt: parts.includes("alt"),
  };
}

function isTypingTarget(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  const tag = t.tagName?.toLowerCase();
  return tag === "input" || tag === "textarea" || t.isContentEditable;
}

/**
 * Hook simple para atajos de teclado globales.
 *
 * Por default los ignora cuando el foco esta en un input/textarea, salvo que
 * el atajo use Ctrl o Cmd (asumimos que Ctrl+algo siempre debe procesarse).
 */
export function useHotkey(
  combo: Combo,
  handler: (e: KeyboardEvent) => void,
  options: { allowWhileTyping?: boolean; deps?: unknown[] } = {},
) {
  const { allowWhileTyping = false, deps = [] } = options;
  useEffect(() => {
    const parsed = parse(combo);

    function onKey(e: KeyboardEvent) {
      const ctrl = e.ctrlKey || e.metaKey;
      const sameKey = e.key.toLowerCase() === parsed.key;
      if (!sameKey) return;
      if (parsed.ctrl !== ctrl) return;
      if (parsed.shift !== e.shiftKey) return;
      if (parsed.alt !== e.altKey) return;
      if (!allowWhileTyping && !ctrl && isTypingTarget(e)) return;
      handler(e);
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combo, allowWhileTyping, ...deps]);
}
